import { html, LitElement, css, unsafeCSS, nothing } from 'lit';
import { ref, createRef } from 'lit/directives/ref.js';
import { live } from 'lit/directives/live.js';
import { FormValidationController } from '@/controllers/FormValidationController.js';
import { TEXTAREA } from '../constants';
import styles from './textareaResize.scss?inline';
import { checkValidity } from '@/components/input/common.js';

export default class SotbitTextareaResize extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static formAssociated = true;

    static properties = {
        _value: { state: true },
        id: { type: String },
        name: { type: String },
        placeholder: { type: String },
        disabled: { type: Boolean },
        spellcheck: { type: String },
        readonly: { type: Boolean },
        rows: { type: Number },
        caption: { type: String },
    };

    constructor() {
        super();

        this._internals = this.attachInternals();
        this._validationController = new FormValidationController(this, {
            getValue: () => this._value,
            getInputRef: () => this.inputRef,
        });

        this._value = '';
        this.type = 'textarea';
        this.id = '';
        this.name = '';
        this.placeholder = '';
        this.inputRef = createRef();
        this.scrollContainerRef = createRef();

        this._handleInput = this._handleInput.bind(this);
    }

    set value(value) {
        this._value = value?.toString() || '';

        this._internals?.setFormValue(value);
        this._validationController.validate();

        this._adjustHeight();
    }

    get value() {
        return this._value;
    }

    formAssociatedCallback() {
        this._setFormValue(this._value);
    }

    formDisabledCallback(disabled) {
        this.toggleAttribute('disabled', disabled);
    }

    formResetCallback() {
        this._value = '';
        this._setFormValue(this._value);
    }

    formStateRestoreCallback(state) {
        this._value = state;
        this._setFormValue(this._value);
    }

    focus(options) {
        this.inputRef?.value?.focus(options);
    }

    checkValidity(reportValidity = true) {
        return checkValidity(this, reportValidity);
    }

    _adjustHeight() {
        if (!this.inputRef.value) {
            return;
        }

        this.inputRef.value.style.height = 'auto'; // reset height to get correct scrollHeight

        // fix height not adjusting then settings this.value property
        requestAnimationFrame(() => {
            this.inputRef.value.style.height = this.inputRef.value.scrollHeight + 'px';
        });
    }

    _setFormValue(value) {
        this._internals?.setFormValue(value);
        this.checkValidity();
    }

    _handleBlur() {
        this.scrollContainerRef.value?.setAttribute('part', 'scroll-container');
    }

    _handleFocus() {
        this.inputRef.value?.focus();
        this.scrollContainerRef.value?.setAttribute('part', 'scroll-container focus');
    }

    _handleInput(event) {
        this._value = event.target.value;

        this._setFormValue(this._value);
        this._adjustHeight();
    }

    _updateInputRef(input) {
        if (input) {
            this.inputRef.value = input;
            this._value = this.getAttribute('value') || this._value;
        }
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('blur', this._handleBlur.bind(this));
        this.addEventListener('focus', this._handleFocus.bind(this));
    }

    render() {
        return html`
            <sotbit-scroll-panel part="scroll-container" ${ref(this.scrollContainerRef)}>
                <textarea
                    .value=${live(this._value)}
                    part="input"
                    id=${this.id || nothing}
                    name=${this.name || nothing}
                    placeholder=${this.placeholder || nothing}
                    rows="${this.rows ?? TEXTAREA.INITIAL_ROWS}"
                    ?disabled="${this.disabled}"
                    ?readonly="${this.readonly}"
                    ?spellcheck="${this.spellcheck}"
                    @input="${this._handleInput}"
                    ${ref(this._updateInputRef)}
                ></textarea>
            </sotbit-scroll-panel>
            ${this.caption ? html`<span part="caption">${this.caption}</span>` : nothing}
        `;
    }
}
